<template>
  <v-dialog v-model="open" width="592px" content-class="share-playlist-modal">
    <v-card class="share-playlist-modal--content">
      <span class="modal-content">
        <!-- Header -->
        <v-toolbar elevation="0">
          <v-toolbar-title class="pa-0">
            <span class="body-bold-medium">{{ $t('message.share') }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="closeDialog">
              <v-icon>panda-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-divider></v-divider>

        <base-alert
          v-if="isPlaylistEmpty"
          type="warning"
          :title="$t('message.playlist.share.warning')"
          :description="$t('message.playlist.share.warningMessage')"
        >
        </base-alert>

        <!-- Content -->
        <v-card-text class="pa-0">
          <v-row class="mt-6" no-gutters>
            <v-col cols="6">
              <div class="body-regular-small access-title">
                {{ $t('message.playlist.share.access.title') }}
              </div>
              <div class="body-regular-smaller access-description">
                {{ $t('message.playlist.share.access.description') }}
              </div>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="accessType"
                :items="accessOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                class="access-select"
                @input="handlePasswordInputChanging($event)"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="showPasswordField" class="mt-6">
            <v-col cols="6">
              <div class="text-subtitle-2 access-title">
                {{ $t('message.playlist.share.password.title') }}
              </div>
              <div class="text-caption access-description">
                {{ $t('message.playlist.share.password.description') }}
              </div>
            </v-col>
            <v-col cols="6" class="pl-0">
              <v-form ref="form" v-model="valid" v-on:submit.prevent>
                <v-input
                  ref="passwordInputShareModal"
                  v-model="password"
                  :type="showPass ? 'text' : 'password'"
                  :placeholder="
                    hasPassword
                      ? $t(
                          'message.playlist.settings.tabs.general.playlistPassword.placeholderDisabled'
                        )
                      : $t('message.playlist.share.password.placeholder')
                  "
                  class="access-select playlist-password"
                  counter="40"
                  max="40"
                  :hide-details="hasPassword"
                  :disabled="hasPassword"
                  :rules="[rules.required]"
                >
                  <template #append>
                    <v-icon
                      v-show="!hasPassword"
                      size="16"
                      @click="showPass = !showPass"
                    >
                      {{
                        !showPass ? 'panda-eye-appearing' : 'panda-eye-hidden'
                      }}</v-icon
                    >
                  </template>
                </v-input>
              </v-form>
              <a
                v-if="hasPassword"
                type="primary"
                class="body-bold-small"
                @click="handlePasswordReset"
                >{{
                  $t(
                    'message.playlist.settings.tabs.general.playlistPassword.resetPassword'
                  )
                }}</a
              >
            </v-col>
          </v-row>

          <!-- Access Link Section -->
          <v-row no-gutters>
            <v-col cols="12">
              <div class="input-label-area"></div>
            </v-col>
            <base-input
              v-model="shareLink"
              :readonly="!!shareLink"
              outlined
              hide-details
              :disabled="!shareLink"
              class="share-link-input"
              :placeholder="$t('message.playlist.share.access.placeholder')"
              :label="$t('message.playlist.share.access.link')"
              append-icon="panda-copy"
              @click:append="copyLink"
            ></base-input>
          </v-row>
        </v-card-text>
      </span>
      <div class="modal-spacer"></div>
      <v-divider class="extended-divider"></v-divider>
      <span class="modal-footer">
        <v-card-actions
          :class="isMobileBreakpoint ? 'mobile-actions' : ''"
          class="pa-0"
        >
          <v-spacer></v-spacer>
          <base-btn
            type="tertiary"
            class="mr-4 px-4 rounded-lg elevation-0"
            :disabled="isFetching"
            color="#EDEEF1"
            @click="closeDialog"
          >
            {{ $t('message.cancel') }}
          </base-btn>
          <base-btn
            type="primary"
            large
            :loading="isFetching"
            :disabled="!hasChanges || !valid || isFetching"
            @click="handleConfirm"
            keep-width-on-loading
          >
            {{ $t('message.actions.publish') }}
          </base-btn>
        </v-card-actions>
      </span>
    </v-card>
  </v-dialog>
</template>

<script>
import playlistApi from '@/services/playlistApi'
import BaseAlert from '@/components/BaseAlert.vue'
import { mapMutations, mapState } from 'vuex'
import i18n from '@/translations/lang'
import _ from 'lodash'

export default {
  name: 'PlaylistShareModal',
  components: {
    BaseAlert,
    BaseInput: () => import('@/components/BaseComponents/BaseInput.vue'),
  },
  data() {
    return {
      open: false,
      accessType: '',
      initialAccessType: '',
      accessOptions: [
        {
          text: this.$t(
            'message.playlist.share.access.options.anyoneWithLink.text'
          ),
          value: 'anyone_with_link',
        },
        {
          text: this.$t(
            'message.playlist.share.access.options.anyoneWithPassword.text'
          ),
          value: 'anyone_with_password',
        },
      ],
      password: '',
      shareLink: '',
      isPlaylistEmpty: false,
      playlist: null,
      isFetching: false,
      valid: true,
      showPass: false,
      hasPassword: false,
      rules: {
        required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      },
    }
  },
  mounted() {
    this.$root.$on('openPlaylistShareModal', (playlist, videos_quantity) => {
      this.isPlaylistEmpty = !videos_quantity
      this.playlist = playlist
      this.accessType = playlist.access_type
      this.initialAccessType = _.clone(playlist.access_type)
      this.hasPassword = this.playlist.access_type === 'anyone_with_password'
      this.handleShareLink(playlist, this.isPlaylistEmpty)
      this.openDialog()
    })
  },
  beforeDestroy() {
    this.$root.$off('openPlaylistShareModal')
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    showPasswordField() {
      return this.accessType === 'anyone_with_password'
    },
    hasChanges() {
      return (
        !_.isEqual(this.initialAccessType, this.accessType) ||
        this.password.length
      )
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleConfirm() {
      this.isFetching = true
      try {
        await playlistApi.updatePlaylist(
          { access_type: this.accessType, password: this.password },
          this.playlist.id
        )
        this.playlist.access_type = this.accessType
        this.password = ''
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.playlist.share.password.success'),
          type: 'success',
        })
        this.$root.$emit('updatePlaylistAccessType', this.playlist.access_type)
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
      this.isFetching = false
      this.closeDialog()
    },
    openDialog() {
      this.open = true
    },
    closeDialog() {
      this.open = false
    },
    copyLink() {
      if (this.shareLink) {
        navigator.clipboard.writeText(this.shareLink)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.copiedToClipboard'),
          type: 'info',
        })
      }
    },
    handleShareLink(playlist, isPlaylistEmpty) {
      if (isPlaylistEmpty) return
      const isProduction = process.env.VUE_APP_ENV === 'production'
      this.shareLink = isProduction
        ? `https://playlist.tv.pandavideo.com/playlist/${playlist.id}`
        : `https://playlist-staging.tv.pandavideo.com/playlist/${playlist.id}`
    },
    handlePasswordInputChanging(val) {
      if (val === 'anyone_with_password') {
        this.$nextTick(() => {
          this.$refs.passwordInputShareModal.$el.querySelector('input').focus()
        })
      } else {
        this.valid = true
      }
    },
    handlePasswordReset() {
      this.hasPassword = false
      this.password = ''
      this.handlePasswordInputChanging('anyone_with_password')
    },
  },
}
</script>

<style lang="scss" scoped>
.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.access-title {
  color: varFigma(neutral-20-dark);
}

.access-description {
  color: varFigma(neutral-40-light);
}

.access-select :deep(.v-select__selection) {
  color: var(--neutral-40-light, #636578);
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
}
</style>

<style lang="scss">
.share-playlist-modal {
  border-radius: 8px !important;
  border: 1px solid varFigma(neutral-95-lightest);

  &--content {
    display: grid;
    grid-template-areas:
      'content'
      'spacer'
      'footer';
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;

    background-color: varFigma(neutral-100-white) !important;
    padding: $spacing-07 !important;
    & header {
      height: unset !important;
      & .v-toolbar__content {
        height: unset !important;
        padding: 0 !important;
        & .v-toolbar__items button {
          justify-content: flex-end !important;
          & .theme--dark {
            color: #666d99 !important;
          }
          & .theme--light {
            color: #abadba !important;
          }
        }
      }
    }
    .input-label-area {
      height: 20px;
    }
    .share-link-input {
      margin-top: $spacing-07;
      &.base-input.v-input.v-input--is-readonly {
        fieldset {
          display: block !important;
        }
        &:hover {
          fieldset {
            border-radius: $spacing-02;
            border: 1px solid varFigma(neutral-95-lightest);
            display: block !important;
          }
        }
      }
      &.base-input.v-input.v-input--is-focused {
        fieldset {
          border: 1px solid varFigma(neutral-95-lightest) !important;
        }
      }
      .v-input__slot {
        margin: $spacing-02 0 !important;
        padding: 0 !important;
        .v-text-field__slot {
          padding: 0 0 0 $spacing-03 !important;
          label {
            left: 0px !important;
          }
        }
        .v-input__append-inner {
          padding: 0 $spacing-03 0 $spacing-03 !important;
          align-self: center !important;
          margin: 0 !important;
          & .v-input__icon.v-input__icon--append {
            width: 16px !important;
            height: 16px !important;
            & .v-icon {
              font-size: 16px !important;
              color: varFigma(neutral-60) !important;
              &.v-icon--disabled {
                color: varFigma(neutral-70) !important;
              }
            }
          }
        }
      }
    }
    .v-divider:not(.extended-divider) {
      margin: $spacing-07 0 !important;
    }
    .extended-divider {
      margin: $spacing-07 -24px $spacing-07 -24px !important;
      max-width: unset !important;
    }
    .modal-content {
      grid-area: content;
    }
    .modal-spacer {
      grid-area: spacer;
      display: none;
    }
    .modal-footer {
      grid-area: footer;
    }
  }
}
</style>
